
export default function Features() {
  const features = [
    {
      icon: 'ri-eye-line',
      title: 'Optical Sensing Technology',
      description:
        'Non-contact water measurement using advanced optical sensors makes magnet manipulation impossible, achieving 99.9% accuracy in precise metering.',
      gradient: 'from-teal-600 to-cyan-600',
      stats: { value: '99.9%', label: 'Measurement Accuracy' },
    },
    {
      icon: 'ri-wifi-line',
      title: 'IoT Real-time Integration',
      description:
        'Real-time data transmission and monitoring through IoT technology, accessible anytime, anywhere via smartphone.',
      gradient: 'from-cyan-600 to-teal-600',
      stats: { value: '24/7', label: 'Real-time Monitoring' },
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Fraud Prevention',
      description:
        'Optical sensing security system completely blocks fraudulent use such as magnet manipulation, ensuring fair metering and revenue protection.',
      gradient: 'from-teal-700 to-teal-500',
      stats: { value: '100%', label: 'Fraud Prevention' },
    },
    {
      icon: 'ri-flask-line',
      title: 'Stainless Steel Material',
      description:
        'Medical-grade stainless steel completely eliminates lead content, achieving more competitive pricing than Japanese products.',
      gradient: 'from-gray-600 to-gray-800',
      stats: { value: '0%', label: 'Lead Content' },
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Data Management',
      description:
        'All information is stored in cloud systems and utilized for Energy Saving to protect the environment and realize a carbon-zero society.',
      gradient: 'from-teal-600 to-emerald-600',
      stats: { value: 'Real-time', label: 'Data Analysis' },
    },
    {
      icon: 'ri-leaf-line',
      title: 'Energy Efficiency · Carbon Zero',
      description:
        'Low-power design enables long-term battery use, an eco-friendly solution realizing the Carbon-free vision.',
      gradient: 'from-emerald-600 to-teal-600',
      stats: { value: '5+ Years', label: 'Battery Life' },
    },
  ];

  return (
    <section
      id="features"
      className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-600 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-600 to-transparent"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block mb-4 px-4 md:px-6 py-2 bg-teal-50 border border-teal-200 rounded-full">
            <span className="text-teal-600 font-semibold tracking-wider text-xs md:text-sm uppercase">
              Core Technology
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            <span className="text-teal-600">Innovative Technology</span> of Smart Digital
            Water Meter
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Implementing next-generation water metering systems with ROBO Asset Grid&apos;s
            optical sensing technology and IoT integration
          </p>
        </div>

        {/* Product Highlight */}
        <div className="mb-10 md:mb-16 bg-gradient-to-br from-[#0a3d4f] to-[#0a2e3d] rounded-2xl md:rounded-3xl p-5 md:p-8 lg:p-12 shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="flex justify-center">
              <div className="relative w-[200px] h-[200px] sm:w-[260px] sm:h-[260px] md:w-[340px] md:h-[340px] lg:w-[380px] lg:h-[380px]">
                <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-[60px]"></div>
                <img
                  src="https://static.readdy.ai/image/2299103fa86076f2ed06076e2d6cbff8/3bb60e1413457df40dbc6ee39e0447f8.png"
                  alt="Optical Sensor Smart Digital Water Meter"
                  title="ROBO Asset Grid Optical Sensing Smart Digital Water Meter Product Detail"
                  className="relative w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
            <div className="text-white space-y-4 md:space-y-6">
              <h3 className="text-xl md:text-3xl font-bold text-center lg:text-left">
                Optical Sensor Smart Digital Water Meter
              </h3>
              <p className="text-sm md:text-base text-teal-200 leading-relaxed text-center lg:text-left">
                A next-generation water meter developed based on the 3C (Carbon-free, Connected,
                Consumer) core vision. Optical sensing technology completely blocks magnet
                manipulation, and IoT integration enables real-time monitoring.
              </p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">99.9%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1">
                    Measurement Accuracy
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">0%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1">Lead Content</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">24/7</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1">
                    Real-time Monitoring
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 md:p-4 text-center">
                  <div className="text-xl md:text-3xl font-black text-teal-400">100%</div>
                  <div className="text-[10px] md:text-xs text-white/70 mt-1">Fraud Prevention</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mb-10 md:mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-teal-300 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 p-5 md:p-8"
            >
              {/* Icon Badge */}
              <div
                className={`w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br ${feature.gradient} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}
              >
                <i className={`${feature.icon} text-2xl md:text-3xl text-white`}></i>
              </div>

              {/* Stats Badge */}
              <div className="absolute top-4 md:top-6 right-4 md:right-6 bg-teal-50 px-2 md:px-3 py-1 md:py-1.5 rounded-lg">
                <div className="text-base md:text-xl font-black text-teal-600">
                  {feature.stats.value}
                </div>
                <div className="text-[8px] md:text-[10px] text-gray-500 font-semibold">
                  {feature.stats.label}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-teal-600 transition-colors duration-300 pr-16 md:pr-20">
                {feature.title}
              </h3>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                {feature.description}
              </p>

              {/* Bottom Accent Line */}
              <div
                className={`mt-4 md:mt-6 h-1 w-0 bg-gradient-to-r ${feature.gradient} group-hover:w-full transition-all duration-500 rounded-full`}
              ></div>

              {/* Hover Glow */}
              <div
                className={`absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${feature.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-10 transition-all duration-500`}
              ></div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href="#contact"
            className="inline-block px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-base md:text-lg font-bold rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Product Inquiry
          </a>
        </div>
      </div>
    </section>
  );
}
